<template>
  <el-dialog
    v-model="isOpen"
    append-to-body
    :show-close="false"
    width="860px"
    class="user-dialog"
    @closed="onClosed"
  >
    <div class="uv-card" v-loading="loading">
      <!-- Left: form island (same shell as auth) -->
      <div class="uv-form">
        <div class="uv-form__body">
          <h2 class="uv-title">{{ pageTitle }}</h2>
          <p class="uv-subtitle">Данные учётной записи</p>

          <el-form label-position="top">
            <el-form-item label="Имя">
              <el-input ref="userName" v-model="user.first_name"></el-input>
            </el-form-item>
            <el-form-item label="Фамилия">
              <el-input v-model="user.last_name"></el-input>
            </el-form-item>
            <el-form-item label="Email">
              <el-input
                :disabled="userId !== 'new'"
                v-model="user.email"
              ></el-input>
            </el-form-item>
            <el-form-item v-if="userId === 'new'" label="Пароль">
              <el-input
                v-model="user.password"
                type="password"
                show-password
              ></el-input>
            </el-form-item>
            <el-form-item label="Роль">
              <el-select
                class="w-full"
                v-model="user.roles"
                multiple
                :multiple-limit="1"
              >
                <el-option
                  v-for="role in roles"
                  :key="role"
                  :label="role"
                  :value="role"
                ></el-option>
              </el-select>
            </el-form-item>
            <div class="flex gap-4 flex-wrap">
              <el-form-item label="Лимит (часы)" style="max-width: 180px">
                <el-input-number
                  v-model="user.time_limit"
                  :min="0"
                  :max="500"
                  class="w-full"
                />
              </el-form-item>
              <el-form-item label="Доступ к batch">
                <el-switch v-model="user.batch_access"></el-switch>
              </el-form-item>
            </div>

            <div class="uv-actions">
              <el-button
                v-if="userId !== 'new' && user.status === 1"
                @click="removeUser"
                :disabled="loading"
                plain
                type="danger"
              >
                Заблокировать
              </el-button>
              <el-button
                v-if="userId !== 'new' && user.status !== 1"
                @click="restoreUser"
                :disabled="loading"
                plain
                type="success"
              >
                Разблокировать
              </el-button>
              <el-button @click="save" :disabled="loading" type="primary">
                Сохранить
              </el-button>
            </div>
          </el-form>
        </div>
      </div>

      <!-- Right: promo / profile -->
      <div class="uv-promo">
        <button class="uv-close" type="button" @click="isOpen = false">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div class="uv-avatar">
          <svg viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="8.5" r="3.6" stroke="#fff" stroke-width="1.6" />
            <path
              d="M5 19.5a7 7 0 0 1 14 0"
              stroke="#fff"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <div class="uv-name">
          {{ user.first_name || "—" }} {{ user.last_name || "" }}
        </div>
        <div class="uv-email">{{ user.email || "Новый пользователь" }}</div>
        <div class="uv-roles" v-if="user.roles && user.roles.length">
          <span v-for="r in user.roles" :key="r" class="uv-role">{{ r }}</span>
        </div>
        <div
          v-if="userId !== 'new'"
          class="uv-status"
          :class="
            user.status === 1 ? 'uv-status--active' : 'uv-status--blocked'
          "
        >
          <span class="uv-status-dot"></span>
          {{ user.status === 1 ? "Активен" : "Заблокирован" }}
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import {
  getUser as _gertUser,
  getRoles as _getRoles,
  createUser as _createUser,
  updateUser as _updateUser,
} from "@/api/users";
import { setAppBlur } from "@/services/appBlur";

export default {
  emits: ["refetch-users", "update:email"],
  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      isOpen: true,
      roles: [],
      loading: false,
      user: {
        email: null,
        password: null,
        roles: ["annotator"],
        first_name: null,
        last_name: null,
        pd: false,
        batch_access: false,
        time_limit: 0,
      },
    };
  },
  computed: {
    pageTitle() {
      return this.userId === "new"
        ? "Создание пользователя"
        : "Редактирование пользователя";
    },
  },
  watch: {
    isOpen(val) {
      // clear blur the instant closing starts, in sync with the dialog
      if (!val) this.clearBlur();
    },
  },
  async mounted() {
    this._blurOn = true;
    setAppBlur(true);
    await this.getRoles();
    if (this.userId !== "new") {
      await this.getUser();
    }
    setTimeout(() => {
      this.$nextTick(() => {
        this.$refs.userName?.focus();
      });
    }, 300);
  },
  beforeUnmount() {
    this.clearBlur();
  },
  methods: {
    clearBlur() {
      if (this._blurOn) {
        this._blurOn = false;
        setAppBlur(false);
      }
    },
    onClosed() {
      this.$router.push({ name: "users-list" });
    },
    async getUser() {
      this.loading = true;
      const user = await _gertUser(this.userId);
      this.loading = false;
      this.user = { ...this.user, ...user };
    },
    async getRoles() {
      this.roles = await _getRoles();
    },
    async createUser() {
      this.loading = true;
      const result = await _createUser(this.user);
      this.loading = false;
      this.postProcessing(result);
    },
    async updateUser() {
      this.loading = true;
      const result = await _updateUser(this.userId, this.user);
      this.loading = false;
      this.postProcessing(result);
    },
    async removeUser() {
      this.loading = true;
      const result = await _updateUser(this.userId, {
        ...this.user,
        status: -1,
      });
      this.loading = false;
      this.postProcessing(result);
    },
    async restoreUser() {
      this.loading = true;
      const result = await _updateUser(this.userId, {
        ...this.user,
        status: 1,
      });
      this.loading = false;
      this.postProcessing(result);
    },
    async save() {
      if (this.userId === "new") {
        await this.createUser();
      } else {
        await this.updateUser();
      }
    },
    postProcessing(result) {
      if (!result._success) {
        return;
      }
      this.$emit("refetch-users");
      this.$router.push({ name: "users-list" });
    },
  },
};
</script>

<style lang="scss">
/* Dialog shell — same outer card as the auth screen, content swapped */
.user-dialog.el-dialog {
  background: transparent;
  box-shadow: none;
  padding: 0;
  overflow: visible;
  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    padding: 0;
  }
}

/* Outer gradient card (mirror of .auth-card) */
.uv-card {
  display: flex;
  align-items: stretch;
  min-height: 540px;
  padding: 6px;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  background: var(--canvas);
}

/* Left: form island (mirror of .auth-form) */
.uv-form {
  flex: 1 1 58%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 30px 36px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 5px;
  box-shadow: var(--shadow-sm);

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
  }
}
.uv-title {
  margin: 0;
  text-align: center;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: var(--foreground);
}
.uv-subtitle {
  margin: 6px 0 22px;
  text-align: center;
  font-size: 13.5px;
  color: var(--muted-foreground);
}
.uv-actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;

  .el-button {
    flex: 1;
    margin: 0;
    height: 42px;
    border-radius: 10px;
    font-weight: 600;
  }

  // Monochrome: primary = solid foreground; secondary = neutral outline
  .el-button--primary {
    background: var(--primary);
    border-color: var(--primary);
    color: var(--primary-foreground);
    &:hover:not(.is-disabled) {
      opacity: 0.9;
      background: var(--primary);
      border-color: var(--primary);
      color: var(--primary-foreground);
    }
  }
  .el-button--danger,
  .el-button--success {
    background: var(--card);
    border-color: var(--border);
    color: var(--muted-foreground);
    &:hover:not(.is-disabled) {
      background: var(--accent);
      border-color: var(--border-strong);
      color: var(--foreground);
    }
  }
}

/* Right: promo / profile (mirror of .auth-promo) */
.uv-promo {
  position: relative;
  flex: 1 1 42%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 28px;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -12%;
    bottom: -12%;
    left: 50%;
    width: 68%;
    transform: translateX(-50%);
    background: radial-gradient(
      55% 55% at 50% 40%,
      rgba(124, 110, 245, 0.11),
      rgba(124, 110, 245, 0.04) 45%,
      transparent 72%
    );
    filter: blur(30px);
    pointer-events: none;
    z-index: 0;
  }
  > * {
    position: relative;
    z-index: 1;
  }
}

/* Dark: drop the colorful glow — keep the panel calm/neutral */
html.dark .uv-promo::before {
  display: none;
}
.uv-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  z-index: 2;
  svg {
    width: 17px;
    height: 17px;
  }
  &:hover {
    background: var(--accent);
    color: var(--foreground);
  }
}
.uv-avatar {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
  svg {
    width: 44px;
    height: 44px;
  }
}
.uv-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--foreground);
  text-align: center;
  letter-spacing: -0.3px;
}
.uv-email {
  font-size: 12.5px;
  color: var(--muted-foreground);
  text-align: center;
  word-break: break-all;
}
.uv-roles {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-top: 2px;
}
.uv-role {
  font-size: 11px;
  font-weight: 600;
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.25);
  border-radius: 999px;
  padding: 2px 10px;
}
.uv-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
  &--active {
    color: #22c55e;
  }
  &--blocked {
    color: #ef4444;
  }
}
.uv-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

@media (max-width: 720px) {
  .uv-promo {
    display: none;
  }
}
</style>
